import { useMemo, useState } from 'react';
import useModal from 'stores/useModal';

import { Button, Card, Form, FormProps, Input, Table } from 'antd';

import { useGetTravelAgencies } from 'hooks/useGetTravelAgencies';
import { getColumnTravelAgency } from 'constants/table';
import { PAGE_SIZE_TRAVEL_AGENCY } from 'services/api/module/travel-agency.api';
import UpdateAndDeleteTravelAgency from 'components/modal/travel-agency-component/update-and-delete-travel-agency-modal';
import CreateTravelAgency from 'components/modal/travel-agency-component/create-travel-agency-modal';

import 'styles/travel-agency.scss';

import { PermissionActionTypes } from 'helper/type/action.type';
import IBButton from 'helper/permission.action';
import { useTranslation } from 'react-i18next';

function TravelAgency() {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const {
    isOpenCreateTravelAgency,
    setIsOpenCreateTravelAgency,
    isOpenUpdateAndDeleteTravelAgency,
    setIsOpenUpdateAndDeleteTravelAgency
  } = useModal();
  const [selectValues, setSelectValues] = useState({});
  const [travelAgencyId, setTravelAgencyId] = useState<string>('');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_TRAVEL_AGENCY);
  const [page, setPage] = useState<number>(1);

  const {
    data: TravelAgencies,
    pagination,
    isLoading
  } = useGetTravelAgencies(selectValues, pageSize, page);

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});
    if (truthyValues) {
      setSelectValues(truthyValues);
    }
    if (page !== 1) {
      setPage(1);
    }
  };

  const onHandleCreateNewTravelAgency = () => {
    setIsOpenCreateTravelAgency(true);
  };

  const onCancelCreateTravelAgency = () => {
    setIsOpenCreateTravelAgency(false);
  };

  const onHandleUpdateAndDeleteTravelAgency = (id: string) => {
    setIsOpenUpdateAndDeleteTravelAgency(true);
    setTravelAgencyId(id);
  };

  const onCancelUpdateAndDeleteTravelAgency = () => {
    setIsOpenUpdateAndDeleteTravelAgency(false);
  };

  const handleChangePage = (page: number) => {
    setPage(page);
    setSelectValues({ ...selectValues, page, limit: pageSize });
  };

  const columnTravelAgency = useMemo(() => {
    return getColumnTravelAgency(t);
  }, [t]);

  return (
    <div className="pms-travel-agency">
      <Card title={t('managePage.travelAgencyPage.title')} style={{ width: '100%' }}>
        <Form form={form} layout={'inline'} onFinish={onFinish} autoComplete="off">
          <div className="pms-travel-agency__filter-wrapper">
            <div className="pms-travel-agency__filter">
              <Form.Item name="id">
                <Input placeholder={t('managePage.travelAgencyPage.taId')} />
              </Form.Item>
              <Form.Item name="name">
                <Input placeholder={t('managePage.travelAgencyPage.taName')} />
              </Form.Item>
              <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                {t('common.actions.submitButton')}
              </Button>
            </div>
            <IBButton
              permission={PermissionActionTypes.TA_MANAGEMENT_CREATE}
              onClick={() => onHandleCreateNewTravelAgency()}
              className="ant-btn-secondary"
            >
              {t('managePage.travelAgencyPage.taCreate')}
            </IBButton>
            <CreateTravelAgency
              isOpen={isOpenCreateTravelAgency}
              onCancel={() => onCancelCreateTravelAgency()}
              t={t}
            />
            <UpdateAndDeleteTravelAgency
              isOpen={isOpenUpdateAndDeleteTravelAgency}
              onCancel={onCancelUpdateAndDeleteTravelAgency}
              travelAgencyId={travelAgencyId}
              t={t}
            />
          </div>
        </Form>
        <div className="pms-travel-agency__table">
          <Table
            loading={isLoading}
            rowKey="id"
            size="small"
            columns={columnTravelAgency}
            dataSource={TravelAgencies}
            onRow={record => {
              return {
                onClick: () => {
                  onHandleUpdateAndDeleteTravelAgency(String(record.id));
                }
              };
            }}
            bordered
            pagination={{
              current: page,
              position: ['bottomCenter'],
              total: pagination.total,
              pageSize: pageSize,
              locale: { items_per_page: `/ ${t('common.page')}` },
              showLessItems: true,
              showTotal: total => `${total} ${t('common.item')}`,
              onChange: (page: number) => handleChangePage(page),
              onShowSizeChange: (current, size) => {
                setPageSize(size);
              }
            }}
            locale={{
              emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
            }}
            scroll={{ x: 2700, y: 'calc(100vh - 400px)' }}
          />
        </div>
      </Card>
    </div>
  );
}

export default TravelAgency;
